import React from 'react';
import { Collapse, Popover, Switch, Tag, notification } from 'antd';
import ListEditItem from '../../../shared/ListEditItem';
import ListItem from '../../../shared/ListItem';
import { renderText } from '../../../shared/DrawerLayout/renderText';
import styles from '../UserInfo.styles';
import { colors } from '../../../../theme/theme';
import LinkToUser from '../../../shared/LinkToUser';
import { findDuplicationReason, getStatusColor } from '../../../../utils';

const { Panel } = Collapse;

interface UserTariffInfoProps {
  selectedUser: any;
  intl: any;
  tariffs: any;
  onTariffUpdate: () => void;
  updateUserUpsellings: (code: string | null) => void;
  onClickTariffFile: () => void;
  onMaxPartnersUpdate: (arg: any) => void;
}

const UserTariffInfo: React.FC<UserTariffInfoProps> = ({
  selectedUser,
  intl,
  tariffs,
  onTariffUpdate,
  updateUserUpsellings,
  onClickTariffFile,
  onMaxPartnersUpdate,
}) => {
  const userTariffAvailableUpsellings = tariffs?.find(
    (tariff: any) => tariff.id === selectedUser?.tariff?.id
  );
  const parsedExtraData = selectedUser?.extraData
    ? JSON.parse(selectedUser.extraData)
    : {};

  const copyInviteLink = () => {
    const el = document.createElement('textarea');
    el.value = selectedUser?.lastGeneratedInviteLink;
    document.body.appendChild(el);
    el.focus();
    el.select();
    try {
      document.execCommand('copy');
      document.body.removeChild(el);
      notification.success({
        message: 'Invoice link Copied',
        style: {
          whiteSpace: 'pre',
        },
      });
    } catch (err) {
      console.log('Oops, unable to copy');
    }
  };

  return (
    <>
      <div className="user-section-title">
        {intl.formatMessage({ id: `fleetadmin.users.info.tariff` })}
      </div>
      <ListEditItem
        testid="TariffInfo"
        label={intl.formatMessage({ id: `fleetadmin.users.info.tariff.label` })}
        leftItem={renderText(selectedUser?.tariff?.name, true)}
        onClickEdit={onTariffUpdate}
      />
      <div className="collapse-container">
        <Collapse bordered={false}>
          <Panel
            header={intl.formatMessage({ id: `fleetadmin.users.tariff.extra` })}
            key="user-id"
          >
            {userTariffAvailableUpsellings?.upsellings?.map(
              (upselling: any) => (
                <ListEditItem
                  key={upselling.code}
                  leftItem={
                    <div className="permission-label">
                      {intl.formatMessage({
                        id: `fleetadmin.users.info.upsellings.${upselling.code}`,
                      })}
                    </div>
                  }
                  rightItem={
                    <Switch
                      checked={selectedUser?.tariff?.upsellings
                        .map((upselling: any) => upselling.code)
                        .includes(upselling.code)}
                      onChange={() =>
                        updateUserUpsellings(
                          selectedUser?.tariff?.upsellings
                            .map((upselling: any) => upselling.code)
                            .includes(upselling.code)
                            ? null
                            : upselling.code
                        )
                      }
                    />
                  }
                />
              )
            )}
            {parsedExtraData &&
              Object.keys(parsedExtraData)
                ?.filter((data) => parsedExtraData?.[data])
                ?.map(
                  (key, index) =>
                    parsedExtraData[key] && (
                      <ListItem
                        key={key}
                        label={intl.formatMessage({
                          id: `Info ${index + 1}`,
                        })}
                        value={renderText(
                          `${intl.formatMessage({
                            id: `fleetadmin.${key.replace(/\s/g, '')}`,
                          })}: ${parsedExtraData[key]}`,
                          true
                        )}
                      />
                    )
                )}
            {['TLA'].includes(selectedUser?.brandCode) && (
              <>
                <ListItem
                  label={intl.formatMessage({
                    id: `fleetadmin.tariff.subscriptionNr`,
                  })}
                  value={renderText(
                    selectedUser?.ebusProductOptions?.[0]?.split(':')?.[1] ||
                      null,
                    true
                  )}
                />
                <div className="tariff-file" onClick={onClickTariffFile}>
                  {intl.formatMessage({ id: `fleetadmin.tariff.file` })}
                </div>
              </>
            )}
          </Panel>
        </Collapse>
      </div>
      {(selectedUser?.tariff?.partnersEligible ||
        selectedUser?.tariff?.partnerSubAccountEligible) &&
        (selectedUser?.mainPartner || selectedUser?.invitedPartner) && (
          <div className="collapse-container">
            <Collapse bordered={false}>
              <Panel
                header={intl.formatMessage({
                  id: `fleetadmin.users.tariff.partner`,
                })}
                key="user-id"
              >
                {selectedUser?.invitedPartner &&
                  selectedUser?.invitedByPartner && (
                    <>
                      <p
                        className="label"
                        style={{ marginLeft: 24, marginBottom: 8 }}
                      >
                        {intl.formatMessage({
                          id: `fleetadmin.users.info.inivitedbypartner`,
                        })}
                      </p>

                      <ListEditItem
                        key={selectedUser?.invitedByPartner?.id}
                        label=""
                        leftItem={
                          <LinkToUser
                            userId={selectedUser?.invitedByPartner?.id}
                            userCode={
                              selectedUser?.invitedByPartner?.referralCode
                            }
                          />
                        }
                        rightItem={
                          <div style={{ display: 'flex' }}>
                            {selectedUser?.invitedByPartner?.merchantSystemData?.map(
                              (merchant) =>
                                merchant?.merchantReference &&
                                merchant?.merchantSystem && (
                                  <div
                                    key={`${merchant?.merchantReference}_${merchant?.merchantSystem}`}
                                    style={{
                                      marginRight: '12px',
                                    }}
                                  >
                                    <Tag
                                      key={merchant?.merchantReference}
                                      style={{
                                        borderColor: '#d3d3d3',
                                        backgroundColor:
                                          'rgba(242, 242, 242, 0.6)',
                                        margin: 2,
                                      }}
                                      className="merchant-tag"
                                    >
                                      {`${merchant?.merchantReference}_${merchant?.merchantSystem}`}
                                    </Tag>
                                  </div>
                                )
                            )}
                            {findDuplicationReason(
                              selectedUser,
                              selectedUser?.invitedByPartner
                            ) && (
                              <Popover
                                content={
                                  <div className="popover">
                                    {intl.formatMessage({
                                      id: findDuplicationReason(
                                        selectedUser,
                                        selectedUser?.invitedByPartner
                                      ),
                                    })}
                                  </div>
                                }
                                placement="top"
                              >
                                <img
                                  className="duplicate-info"
                                  src="/images/drawer/info.svg"
                                  alt="info"
                                />
                              </Popover>
                            )}
                            <Tag
                              color={getStatusColor(
                                selectedUser?.invitedByPartner?.accountStatus
                              )}
                              style={{ height: '22px' }}
                            >
                              {selectedUser?.invitedByPartner?.accountStatus}
                            </Tag>
                            {selectedUser?.invitedByPartner?.accountStatus ===
                              'blocked' && (
                              <Popover
                                content={
                                  <div className="popover">
                                    {intl.formatMessage({
                                      id: `fleetadmin.user.blocked.${
                                        selectedUser?.invitedByPartner
                                          ?.blockReasonDetail ||
                                        selectedUser?.invitedByPartner
                                          ?.blockReason ||
                                        'other'
                                      }`,
                                    })}
                                  </div>
                                }
                                placement="left"
                              >
                                <img
                                  style={
                                    !selectedUser?.invitedByPartner
                                      ?.blockReason ||
                                    selectedUser?.invitedByPartner
                                      ?.blockReason === 'other'
                                      ? {
                                          marginLeft: 10,
                                          width: '14px',
                                          height: '14px',
                                        }
                                      : {
                                          marginLeft: 10,
                                          width: '16px',
                                          height: '16px',
                                        }
                                  }
                                  src={`/images/user-blocked-reasons/${
                                    selectedUser?.invitedByPartner
                                      ?.blockReasonDetail === 'creditCheck'
                                      ? 'creditCheck'
                                      : selectedUser?.invitedByPartner
                                          ?.blockReason || 'other'
                                  }.svg`}
                                  alt="block reason"
                                />
                              </Popover>
                            )}
                          </div>
                        }
                      />
                    </>
                  )}
                {selectedUser?.mainPartner && (
                  <>
                    {selectedUser?.lastGeneratedInviteLink ? (
                      <ListItem
                        testid="receiptNo"
                        label={intl.formatMessage({
                          id: 'fleetadmin.users.tariff.lastlink',
                        })}
                        value={
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <span style={{ color: colors.blue }}>
                              {`${selectedUser?.lastGeneratedInviteLink?.slice(
                                0,
                                35
                              )}...` || '-'}
                            </span>
                            <img
                              src={'/images/car-editor/location.svg'}
                              alt="location"
                              onClick={copyInviteLink}
                              style={{ cursor: 'pointer' }}
                            />
                          </div>
                        }
                      />
                    ) : (
                      <ListItem
                        testid="receiptNo"
                        label={intl.formatMessage({
                          id: 'fleetadmin.users.tariff.lastlink',
                        })}
                        value={
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <span style={{ color: colors.black }}>
                              {intl.formatMessage({
                                id: `fleetadmin.users.tariff.lastlink.not.created`,
                              })}
                            </span>
                          </div>
                        }
                      />
                    )}

                    <ListEditItem
                      label={intl.formatMessage({
                        id: `fleetadmin.users.info.max.users`,
                      })}
                      leftItem={renderText(selectedUser?.maxPartners, false)}
                      onClickEdit={() =>
                        onMaxPartnersUpdate('TariffSettingsModal')
                      }
                    />

                    <ListEditItem
                      label={intl.formatMessage({
                        id: `fleetadmin.users.info.remaining`,
                      })}
                      leftItem={renderText(
                        selectedUser?.remainingPartners,
                        false
                      )}
                    />
                    <p
                      className="label"
                      style={{ marginLeft: 24, marginBottom: 8 }}
                    >
                      {intl.formatMessage({
                        id: `fleetadmin.users.info.inivitedpartner`,
                      })}
                    </p>

                    {selectedUser?.invitedPartners?.length > 0 ? (
                      selectedUser?.invitedPartners?.map((invited) => (
                        <ListEditItem
                          key={invited.id}
                          label=""
                          leftItem={
                            <LinkToUser
                              userId={invited.id}
                              userCode={invited.referralCode}
                            />
                          }
                          rightItem={
                            <div style={{ display: 'flex' }}>
                              {invited?.merchantSystemData?.map(
                                (merchant) =>
                                  merchant?.merchantReference &&
                                  merchant?.merchantSystem && (
                                    <div
                                      key={`${merchant?.merchantReference}_${merchant?.merchantSystem}`}
                                      style={{
                                        marginRight: '12px',
                                      }}
                                    >
                                      <Tag
                                        key={merchant?.merchantReference}
                                        style={{
                                          borderColor: '#d3d3d3',
                                          backgroundColor:
                                            'rgba(242, 242, 242, 0.6)',
                                          margin: 2,
                                        }}
                                        className="merchant-tag"
                                      >
                                        {`${merchant?.merchantReference}_${merchant?.merchantSystem}`}
                                      </Tag>
                                    </div>
                                  )
                              )}
                              {findDuplicationReason(selectedUser, invited) && (
                                <Popover
                                  content={
                                    <div className="popover">
                                      {intl.formatMessage({
                                        id: findDuplicationReason(
                                          selectedUser,
                                          invited
                                        ),
                                      })}
                                    </div>
                                  }
                                  placement="top"
                                >
                                  <img
                                    className="duplicate-info"
                                    src="/images/drawer/info.svg"
                                    alt="info"
                                  />
                                </Popover>
                              )}
                              <Tag
                                color={getStatusColor(invited?.accountStatus)}
                                style={{ height: '22px' }}
                              >
                                {invited.accountStatus}
                              </Tag>
                              {invited?.accountStatus === 'blocked' && (
                                <Popover
                                  content={
                                    <div className="popover">
                                      {intl.formatMessage({
                                        id: `fleetadmin.user.blocked.${
                                          invited?.blockReasonDetail ||
                                          invited?.blockReason ||
                                          'other'
                                        }`,
                                      })}
                                    </div>
                                  }
                                  placement="left"
                                >
                                  <img
                                    style={
                                      !invited?.blockReason ||
                                      invited?.blockReason === 'other'
                                        ? {
                                            marginLeft: 10,
                                            width: '14px',
                                            height: '14px',
                                          }
                                        : {
                                            marginLeft: 10,
                                            width: '16px',
                                            height: '16px',
                                          }
                                    }
                                    src={`/images/user-blocked-reasons/${
                                      invited?.blockReasonDetail ===
                                      'creditCheck'
                                        ? 'creditCheck'
                                        : invited?.blockReason || 'other'
                                    }.svg`}
                                    alt="block reason"
                                  />
                                </Popover>
                              )}
                            </div>
                          }
                        />
                      ))
                    ) : (
                      <div className="empty-wrapper">
                        <img
                          alt="no-data"
                          className="empty"
                          src="/images/table/empty.svg"
                          width="50"
                          height="50"
                        />
                        {intl.formatMessage({
                          id: `fleetadmin.users.info.empty`,
                        })}
                      </div>
                    )}
                  </>
                )}
              </Panel>
            </Collapse>
          </div>
        )}
      <style jsx>{styles}</style>
    </>
  );
};

export default UserTariffInfo;
